/**
 * docs.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Browse Docs — Route: /docs
 * Placeholder article content; wire `ARTICLES` up to a real docs table/CMS later.
 */
import { useState, useEffect, useMemo } from "react";
import {
  Search, BookOpen, ChevronRight, Rocket, CandlestickChart, Wallet,
  Users, ShoppingBag, Shield, ArrowLeft, Clock,
} from "lucide-react";
import { BASE_CSS, Sidebar, Topbar, DEFAULT_SETTINGS, buildThemeVars, applyThemeVars, useCurrentUser } from "../shared/AppShell";

const PAGE_CSS = `
  .dc-hero-search { display:flex; gap:10px; max-width:520px; margin-top:20px; }
  .dc-hero-input-wrap { position:relative; flex:1; }
  .dc-hero-input {
    width:100%; background:rgba(255,255,255,.05); border:1px solid var(--border2);
    color:var(--text); border-radius:var(--r-sm); padding:10px 14px 10px 40px;
    font-size:13px; outline:none; transition:border-color .15s; font-family:var(--sans);
  }
  .dc-hero-input::placeholder { color:var(--muted); }
  .dc-hero-input:focus { border-color:var(--accent); background:rgba(var(--accent-rgb),.04); }
  .dc-hero-icon { position:absolute; left:13px; top:50%; transform:translateY(-50%); color:var(--muted); }

  .dc-layout { display:grid; grid-template-columns:220px 1fr; gap:var(--layout-gap); align-items:start; }
  .dc-cats { display:flex; flex-direction:column; gap:2px; position:sticky; top:0; }
  .dc-cat-btn {
    display:flex; align-items:center; gap:10px; padding:10px 14px;
    font-size:13px; font-weight:500; color:var(--muted); border-radius:var(--r-sm);
    cursor:pointer; transition:all .15s; text-align:left;
  }
  .dc-cat-btn:hover { background:var(--accent-glow); color:var(--text); }
  .dc-cat-btn.active { background:var(--accent-dim); color:var(--accent); }
  .dc-cat-count { margin-left:auto; font-size:10px; color:var(--faint); }

  .dc-article-list { display:flex; flex-direction:column; gap:var(--layout-gap); }
  .dc-article {
    display:flex; align-items:flex-start; gap:14px; padding:16px 20px;
    border-bottom:1px solid var(--border); cursor:pointer; transition:background .15s;
  }
  .dc-article:last-child { border-bottom:none; }
  .dc-article:hover { background:var(--accent-glow); }
  .dc-article-title { font-size:13.5px; font-weight:600; margin-bottom:4px; }
  .dc-article-desc { font-size:12px; color:var(--muted); line-height:1.5; }
  .dc-article-meta { font-size:11px; color:var(--faint); display:flex; align-items:center; gap:6px; margin-top:6px; }

  .dc-detail-body { padding:24px; font-size:13.5px; color:var(--text); line-height:1.8; }
  .dc-detail-body p { margin-bottom:14px; color:var(--muted); }
  .dc-detail-body h3 { font-size:14px; margin:18px 0 8px; color:var(--text); }
  .dc-empty { padding:48px 24px; text-align:center; color:var(--muted); font-size:13px; }

  @media (max-width:900px) { .dc-layout { grid-template-columns:1fr; } }
`;

const CATEGORIES = [
  { key: "getting-started", label: "Getting Started", Icon: Rocket },
  { key: "trading", label: "Trading & Terminal", Icon: CandlestickChart },
  { key: "wallets", label: "Wallets & Deposits", Icon: Wallet },
  { key: "copy-trading", label: "Copy Trading", Icon: Users },
  { key: "marketplace", label: "Marketplace", Icon: ShoppingBag },
  { key: "security", label: "Security & Account", Icon: Shield },
];

const ARTICLES = [
  { cat: "getting-started", title: "Creating your account", desc: "How signup, email verification, and your first wallet setup work.", read: "3 min", body: "Sign up with your email and a password of at least 8 characters. After verifying your email via the link we send, your account is created with a default USD wallet at a zero balance. You can add more currency wallets from Settings → Wallets at any time." },
  { cat: "getting-started", title: "Understanding your dashboard", desc: "A tour of the portfolio summary, wallet balance, and recent activity.", read: "4 min", body: "Your dashboard shows your total portfolio value, today's P&L, and a snapshot of open positions. The wallet card in the sidebar always reflects your preferred currency balance." },
  { cat: "trading", title: "Placing your first trade", desc: "Market vs. limit orders, lots, and how open positions are tracked.", read: "5 min", body: "From the Trading Terminal, choose a symbol, direction, and lot size, then submit a market or limit order. Open positions update in real time using live price feeds, and P&L is calculated against your entry price." },
  { cat: "trading", title: "Reading the trading terminal", desc: "Live charts, order book, and position management explained.", read: "6 min", body: "The terminal combines a live TradingView chart with your open positions table. Use the position row's close button to exit early, or set stop loss / take profit levels when placing the order." },
  { cat: "wallets", title: "Depositing funds", desc: "Submitting payment proof for crypto deposits and confirmation timing.", read: "3 min", body: "After sending a crypto deposit to the provided address, upload a screenshot of your payment as proof. Our team confirms deposits, typically within a few hours, and your wallet balance updates automatically." },
  { cat: "wallets", title: "Withdrawal timelines", desc: "How long withdrawals take and what can delay them.", read: "2 min", body: "Withdrawals are reviewed manually for security. Standard requests are processed within 24–48 hours. Delays are usually due to pending identity verification." },
  { cat: "copy-trading", title: "How copy trading works", desc: "Allocating funds to a trader and how their trades mirror to your account.", read: "4 min", body: "When you copy a trader, a portion of your balance is allocated to mirroring their open and future positions proportionally. You can stop copying at any time from the Copy Trading page." },
  { cat: "marketplace", title: "Subscribing to a plan", desc: "What marketplace subscriptions unlock and how billing works.", read: "3 min", body: "Marketplace plans unlock premium signals, strategies, or trader access. Subscriptions renew automatically each billing cycle unless cancelled from Settings." },
  { cat: "security", title: "Enabling two-factor authentication", desc: "Securing your account with an authenticator app.", read: "2 min", body: "Go to Settings → Security → Two-Factor Authentication and scan the QR code with an authenticator app. Save your backup codes somewhere safe — they're the only way back in if you lose your device." },
];

export default function DocsPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState(null);
  const [activeArticle, setActiveArticle] = useState(null);
  const user = useCurrentUser();

  useEffect(() => { applyThemeVars(buildThemeVars(DEFAULT_SETTINGS)); }, []);

  const filtered = useMemo(() => {
    return ARTICLES.filter((a) => {
      if (activeCat && a.cat !== activeCat) return false;
      if (query && !`${a.title} ${a.desc}`.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [query, activeCat]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: BASE_CSS + PAGE_CSS }} />
      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 299 }} />}
      <div className="in-shell">
        <Sidebar open={sidebarOpen} active="/support" />
        <div className="in-right">
          <Topbar onMenu={() => setSidebarOpen((v) => !v)} user={user} title="Browse" accent="Docs" />
          <main className="in-main">
            <div className="pg-back" onClick={() => (window.location.href = "/support")}>
              <ArrowLeft size={13} /> Back to Help Center
            </div>

            <div className="pg-hero">
              <div className="pg-hero-grid" />
              <div className="pg-hero-content">
                <div className="pg-hero-label">Documentation</div>
                <div className="pg-hero-title">Guides & <em>how-tos</em></div>
                <div className="pg-hero-sub">Step-by-step articles covering everything from your first deposit to advanced copy trading.</div>
                <div className="dc-hero-search">
                  <div className="dc-hero-input-wrap">
                    <Search size={16} className="dc-hero-icon" />
                    <input className="dc-hero-input" placeholder="Search articles…" value={query} onChange={(e) => setQuery(e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            <div className="dc-layout">
              <div className="dc-cats">
                <div className="dc-cat-btn" style={{ fontWeight: 700, color: !activeCat ? "var(--accent)" : "var(--muted)", background: !activeCat ? "var(--accent-dim)" : "transparent" }} onClick={() => { setActiveCat(null); setActiveArticle(null); }}>
                  <BookOpen size={15} /> All articles <span className="dc-cat-count">{ARTICLES.length}</span>
                </div>
                {CATEGORIES.map((c) => (
                  <div key={c.key} className={`dc-cat-btn${activeCat === c.key ? " active" : ""}`} onClick={() => { setActiveCat(c.key); setActiveArticle(null); }}>
                    <c.Icon size={15} /> {c.label}
                    <span className="dc-cat-count">{ARTICLES.filter((a) => a.cat === c.key).length}</span>
                  </div>
                ))}
              </div>

              <div className="pg-card">
                {activeArticle ? (
                  <>
                    <div className="pg-card-head">
                      <div className="pg-card-title" onClick={() => setActiveArticle(null)} style={{ cursor: "pointer" }}>
                        <ArrowLeft size={15} /> {activeArticle.title}
                      </div>
                    </div>
                    <div className="dc-detail-body">
                      <p>{activeArticle.body}</p>
                      <div style={{ fontSize: 11, color: "var(--faint)", display: "flex", alignItems: "center", gap: 6 }}>
                        <Clock size={12} /> {activeArticle.read} read
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="dc-article-list">
                    {filtered.length === 0 ? (
                      <div className="dc-empty">No articles match your search.</div>
                    ) : (
                      filtered.map((a, i) => (
                        <div key={i} className="dc-article" onClick={() => setActiveArticle(a)}>
                          <div style={{ flex: 1 }}>
                            <div className="dc-article-title">{a.title}</div>
                            <div className="dc-article-desc">{a.desc}</div>
                            <div className="dc-article-meta"><Clock size={11} /> {a.read} read</div>
                          </div>
                          <ChevronRight size={15} style={{ color: "var(--muted)", marginTop: 2 }} />
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}